import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/ApiService';
import { MapPanel } from '../Search/MapPanel';
import { getOrderedImages } from '../../utils/mediaUtils';
import { getImovelModalidades, getModalidadeIconSvg, getModalidadeLabel } from '../../utils/modalidadeUtils';
import { formatDescriptionForDisplay } from '../../utils/descriptionFormatter';
import { removeAddressNumber } from '../../utils/addressUtils';
import './PropertyDetailsModal.css';

interface PropertyDetailsModalProps {
  propertyId: string | null;
  propertyData?: any;
  isOpen: boolean;
  onClose: () => void;
}

export const PropertyDetailsModal: React.FC<PropertyDetailsModalProps> = ({
  propertyId,
  propertyData,
  isOpen,
  onClose
}) => {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen || !propertyId) return null;

  return createPortal(
    <div className="property-details-modal-overlay" onClick={handleOverlayClick}>
      <div className="property-details-modal">
        <div className="property-details-modal-header">
          <h2>Detalhes do Imóvel</h2>
          <button 
            className="property-details-modal-close"
            onClick={onClose}
            aria-label="Fechar modal"
          >
            ✕
          </button>
        </div>
        
        <div className="property-details-modal-content">
          <PropertyDetailsModalContent propertyId={propertyId} propertyData={propertyData} />
        </div>
      </div>
    </div>,
    document.body
  );
};

interface PropertyDetailsModalContentProps {
  propertyId: string;
  propertyData?: any;
}

const PropertyDetailsModalContent: React.FC<PropertyDetailsModalContentProps> = ({ propertyId, propertyData }) => {
  const [propertyDetails, setPropertyDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'fotos' | 'mapa'>('fotos');
  const [isZoomMode, setIsZoomMode] = useState(false);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);

  const handleWhatsApp = () => {
    if (!propertyDetails) return;
    const phoneNumber = '5547988569815';
    const message = `Olá! Tenho interesse no imóvel ${propertyDetails.titulo} (Cód: ${propertyDetails.codigo})`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  useEffect(() => {
    // Se temos os dados já disponíveis, usar eles
    if (propertyData) {
      console.log('✅ Modal - Dados recebidos:', {
        id: propertyData.id,
        titulo: propertyData.titulo,
        midias: propertyData.midias,
        midiasLength: propertyData.midias?.length || 0
      });
      setPropertyDetails(propertyData);
      setLoading(false);
      setError(null);
      
      // Registrar visualização quando o modal abrir com dados disponíveis
      if (propertyData.codigo) {
        apiService.registrarVisualizacao(propertyData.codigo).catch((err) => {
          // Erro silencioso - não impacta a experiência do usuário
          console.warn('⚠️ Não foi possível registrar visualização:', err);
        });
      }
    } else {
      // Caso contrário, fazer requisição (fallback)
      const fetchPropertyDetails = async () => {
        try {
          setLoading(true);
          const response = await apiService.getImovel(propertyId);
          if (response.success && response.data) {
            setPropertyDetails(response.data);
            
            // Registrar visualização após carregar os dados
            if (response.data.codigo) {
              apiService.registrarVisualizacao(response.data.codigo).catch((err) => {
                // Erro silencioso - não impacta a experiência do usuário
                console.warn('⚠️ Não foi possível registrar visualização:', err);
              });
            }
          } else {
            setError('Erro ao carregar detalhes do imóvel');
          }
        } catch (err: any) {
          setError(`Erro ao carregar detalhes do imóvel: ${err.message}`);
        } finally {
          setLoading(false);
        }
      };

      fetchPropertyDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, propertyData]);

  // Funções de navegação de imagens - calcular imagens aqui
  const imagens = propertyDetails ? getOrderedImages(propertyDetails) : [];
  
  const goToPreviousImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? imagens.length - 1 : prev - 1
    );
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === imagens.length - 1 ? 0 : prev + 1
    );
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  // Funções para zoom
  const openZoom = (index: number) => {
    setZoomImageIndex(index);
    setIsZoomMode(true);
  };

  const closeZoom = () => {
    setIsZoomMode(false);
  };

  const goToPreviousImageZoom = () => {
    setZoomImageIndex((prev) => 
      prev === 0 ? imagens.length - 1 : prev - 1
    );
  };

  const goToNextImageZoom = () => {
    setZoomImageIndex((prev) => 
      prev === imagens.length - 1 ? 0 : prev + 1
    );
  };

  // Handle keyboard for zoom mode
  useEffect(() => {
    if (!isZoomMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeZoom();
      } else if (e.key === 'ArrowLeft') {
        goToPreviousImageZoom();
      } else if (e.key === 'ArrowRight') {
        goToNextImageZoom();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isZoomMode, imagens.length]);

  if (loading) {
    return (
      <div className="property-details-modal-loading">
        <div className="loading-spinner"></div>
        <p>Carregando detalhes do imóvel...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="property-details-modal-error">
        <h3>Erro</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!propertyDetails) {
    return (
      <div className="property-details-modal-error">
        <h3>Imóvel não encontrado</h3>
        <p>O imóvel solicitado não foi encontrado.</p>
      </div>
    );
  }

  return (
    <>
      {/* Zoom View */}
      {isZoomMode && imagens.length > 0 && (
        <div className="property-zoom-overlay" onClick={closeZoom}>
          <div className="property-zoom-content" onClick={(e) => e.stopPropagation()}>
            <button className="property-zoom-close" onClick={closeZoom} aria-label="Fechar zoom">
              ✕
            </button>
            
            {zoomImageIndex >= 0 && zoomImageIndex < imagens.length && (
              <img 
                src={getImageUrl(imagens[zoomImageIndex].url)} 
                alt={imagens[zoomImageIndex].titulo || 'Imagem em zoom'}
                className="property-zoom-image"
              />
            )}

            {imagens.length > 1 && (
              <>
                <button 
                  className="property-zoom-nav property-zoom-prev" 
                  onClick={goToPreviousImageZoom}
                  aria-label="Imagem anterior"
                >
                  ‹
                </button>
                <button 
                  className="property-zoom-nav property-zoom-next" 
                  onClick={goToNextImageZoom}
                  aria-label="Próxima imagem"
                >
                  ›
                </button>
                
                <div className="property-zoom-counter">
                  {zoomImageIndex + 1} / {imagens.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="property-details-modal-body">
        {/* Header do imóvel */}
        <div className="property-modal-header">
        <div className="property-modal-title-section">
          <h1>{propertyDetails.titulo || 'Título não disponível'}</h1>
          <span className="property-modal-code">CÓD: {propertyDetails.codigo}</span>
        </div>
        <div className="property-modal-location">
          <div className="property-modal-address">
            <div className="address-details">
              <p className="address-street">{removeAddressNumber(propertyDetails.endereco) || 'Endereço não disponível'}</p>
              <p className="address-city">
                {propertyDetails.bairro && `${propertyDetails.bairro}, `}
                {propertyDetails.cidade && `${propertyDetails.cidade}, `}
                {propertyDetails.estado && `${propertyDetails.estado}`}
                {propertyDetails.cep && ` - ${propertyDetails.cep}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sistema de Tabs */}
      <div className="property-modal-tabs">
        <div className="property-modal-tab-header">
          <button 
            className={`property-modal-tab ${activeTab === 'fotos' ? 'active' : ''}`}
            onClick={() => setActiveTab('fotos')}
          >
            Fotos
          </button>
          <button 
            className={`property-modal-tab ${activeTab === 'mapa' ? 'active' : ''}`}
            onClick={() => setActiveTab('mapa')}
          >
            Mapa
          </button>
        </div>

        <div className="property-modal-tab-content">
          {/* Tab Fotos */}
          {activeTab === 'fotos' && (
            <div className="property-modal-tab-panel">
                  {/* Galeria de imagens com navegação */}
              {imagens.length > 0 ? (
                <div className="property-modal-gallery">
                  <div className="property-modal-main-image">
                    {currentImageIndex >= 0 && currentImageIndex < imagens.length && (
                      <img 
                        src={getImageUrl(imagens[currentImageIndex].url)} 
                        alt={imagens[currentImageIndex].titulo || 'Imagem do imóvel'}
                        crossOrigin="anonymous"
                        onClick={() => openZoom(currentImageIndex)}
                        style={{ cursor: 'pointer' }}
                        onLoad={() => {
                          console.log('✅ Modal - Imagem carregada com sucesso:', imagens[currentImageIndex].url);
                        }}
                        onError={(e) => {
                          console.error('❌ Modal - Erro ao carregar imagem:', {
                            urlOriginal: imagens[currentImageIndex].url,
                            urlFinal: getImageUrl(imagens[currentImageIndex].url),
                            erro: e
                          });
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    
                    {/* Botões de navegação */}
                    {imagens.length > 1 && (
                      <>
                        <button 
                          className="property-modal-nav-button property-modal-prev" 
                          onClick={goToPreviousImage}
                          aria-label="Imagem anterior"
                        >
                          ‹
                        </button>
                        <button 
                          className="property-modal-nav-button property-modal-next" 
                          onClick={goToNextImage}
                          aria-label="Próxima imagem"
                        >
                          ›
                        </button>
                      </>
                    )}
                    
                    {/* Contador de imagens */}
                    {imagens.length > 1 && (
                      <div className="property-modal-image-counter">
                        {currentImageIndex + 1} / {imagens.length}
                      </div>
                    )}
                  </div>
                  
                  {/* Miniaturas */}
                  {imagens.length > 1 && (
                    <div className="property-modal-thumbnails">
                      {imagens.map((media: any, index: number) => (
                        <img
                          key={index}
                          src={getImageUrl(media.url)}
                          alt={media.titulo || `Thumbnail ${index + 1}`}
                          crossOrigin="anonymous"
                          className={index === currentImageIndex ? 'active' : ''}
                          onClick={() => {
                            goToImage(index);
                            openZoom(index);
                          }}
                          style={{ cursor: 'pointer' }}
                          onError={(e) => {
                            console.error('❌ Modal - Erro ao carregar thumbnail:', media.url);
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="property-modal-no-image">
                  <span>Sem imagens disponíveis</span>
                </div>
              )}

              {/* Informações básicas - Exibidas automaticamente após as fotos */}
              {propertyDetails && (
                <div className="property-modal-info">
                <div className="property-modal-section">
                  <h3 className="section-title">Características do Imóvel</h3>
                  <div className="property-modal-features">
                    {propertyDetails.quartos && propertyDetails.tipo_imovel !== 'comercial' && (
                      <div className="feature-item feature-quartos">
                        <svg className="feature-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 12C2 9.79086 3.79086 8 6 8H18C20.2091 8 22 9.79086 22 12V20C22 20.5523 21.5523 21 21 21H19C18.4477 21 18 20.5523 18 20V16H6V20C6 20.5523 5.55228 21 5 21H3C2.44772 21 2 20.5523 2 20V12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span className="feature-text">{propertyDetails.quartos} quarto{propertyDetails.quartos > 1 ? 's' : ''}</span>
                      </div>
                    )}
                    {propertyDetails.banheiros && (
                      <div className="feature-item feature-banheiros">
                        <svg className="feature-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 12C9 13.1046 8.10457 14 7 14C5.89543 14 5 13.1046 5 12C5 10.8954 5.89543 10 7 10C8.10457 10 9 10.8954 9 12Z" stroke="currentColor" strokeWidth="2"/>
                          <path d="M4 20V12C4 8.68629 6.68629 6 10 6H14C17.3137 6 20 8.68629 20 12V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span className="feature-text">{propertyDetails.banheiros} banheiro{propertyDetails.banheiros > 1 ? 's' : ''}</span>
                      </div>
                    )}
                    {propertyDetails.area_util && (
                      <div className="feature-item feature-area">
                        <svg className="feature-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 7V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M3 21V19C3 17.8954 3.89543 17 5 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M21 21L3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M21 17H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M15 21V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M9 3V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span className="feature-text">{propertyDetails.area_util} m²</span>
                      </div>
                    )}
                    {propertyDetails.vagas_garagem && (
                      <div className="feature-item feature-garagem">
                        <svg className="feature-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 7V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M3 21V19C3 17.8954 3.89543 17 5 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M21 21L3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M21 17H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M15 21V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M9 3V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span className="feature-text">{propertyDetails.vagas_garagem} vaga{propertyDetails.vagas_garagem > 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tipo de Imóvel */}
                {propertyDetails.tipo_imovel && (
                  <div className="property-modal-type-name">
                    {propertyDetails.tipo_imovel.toUpperCase()}
                  </div>
                )}

                {/* Preço */}
                {propertyDetails.valor_aluguel && propertyDetails.valor_aluguel > 0 && (
                  <div className="property-modal-price">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(propertyDetails.valor_aluguel)} / mês
                  </div>
                )}
                {propertyDetails.valor_venda && propertyDetails.valor_venda > 0 && (
                  <div className="property-modal-price">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(propertyDetails.valor_venda)}
                  </div>
                )}

                {/* Modalidades de Aluguel */}
                {(() => {
                  const modalidades = getImovelModalidades(propertyDetails);
                  return modalidades.length > 0 ? (
                    <div className="property-modal-section">
                      <h3 className="section-title">Modalidades de Aluguel Aceitas</h3>
                      <div className="property-modalidades-list">
                        {modalidades.map((modalidade, index) => (
                          <div key={modalidade.id || index} className="modalidade-badge-item">
                            {getModalidadeIconSvg(modalidade.tipo, 20)}
                            <span className="modalidade-name-badge">
                              {modalidade.nome || getModalidadeLabel(modalidade.tipo)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Comodidades */}
                {(propertyDetails.aceita_pet || propertyDetails.mobiliado || propertyDetails.ar_condicionado || propertyDetails.internet) && (
                  <div className="property-modal-section">
                    <h3 className="section-title">Comodidades</h3>
                    <div className="property-amenities">
                      {propertyDetails.aceita_pet && (
                        <div className="amenity-item amenity-pets">
                          <span className="amenity-text">Aceita pets</span>
                        </div>
                      )}
                      {propertyDetails.mobiliado && (
                        <div className="amenity-item amenity-mobiliado">
                          <span className="amenity-text">Mobiliado</span>
                        </div>
                      )}
                      {propertyDetails.ar_condicionado && (
                        <div className="amenity-item amenity-ar">
                          <span className="amenity-text">Ar condicionado</span>
                        </div>
                      )}
                      {propertyDetails.internet && (
                        <div className="amenity-item amenity-internet">
                          <span className="amenity-text">Internet</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Botão Tirar Dúvidas */}
                <div className="property-modal-section">
                  <button 
                    className="property-action-btn" 
                    onClick={handleWhatsApp}
                    style={{ width: '100%', marginBottom: '24px' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="currentColor"/>
                    </svg>
                    Tirar dúvidas
                  </button>
                </div>

                {/* Descrição */}
                {propertyDetails.descricao && (
                  <div className="property-modal-section">
                    <h3 className="section-title">Descrição</h3>
                    <div className="property-modal-description">
                      <div className="description-text" style={{ whiteSpace: 'pre-wrap' }}>
                        {formatDescriptionForDisplay(propertyDetails.descricao)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* Tab Mapa */}
          {activeTab === 'mapa' && (
            <div className="property-modal-tab-panel">
              <div className="property-modal-map-container">
                {propertyDetails ? (
                  <MapPanel 
                    properties={[propertyDetails]}
                    searchResults={[propertyDetails]}
                    zoomLevel={12}
                    center={{ lat: -26.9906, lng: -48.6348 }}
                  />
                ) : (
                  <div className="property-modal-map-loading">
                    <div className="loading-spinner"></div>
                    <p>Carregando mapa...</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

// Função para construir URL da imagem (idêntica à PropertyCard)
const getImageUrl = (midiaUrl: string) => {
  if (!midiaUrl) return '';

  // Determinar a base da API a partir das envs
  const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '') || 'http://localhost:3005';
  const rawApiBaseEnv = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  const apiBase = rawApiBaseEnv || (rawBackend.endsWith('/api') ? rawBackend : `${rawBackend}/api`);

  // Extrair origin e path da base da API
  let origin = '';
  let apiPath = '/api';
  try {
    const u = new URL(apiBase);
    origin = u.origin;
    apiPath = u.pathname.replace(/\/+$/, '') || '/api';
  } catch {
    origin = '';
    apiPath = '/api';
  }

  // Normalizar caminho de mídia para sempre usar /uploads a partir do início
  const rawPath = midiaUrl.startsWith('/') ? midiaUrl : `/${midiaUrl}`;
  const idx = rawPath.toLowerCase().indexOf('/uploads');
  const uploadsPath = idx >= 0 ? rawPath.substring(idx) : rawPath;
  const cleanPath = uploadsPath.replace(/^(\/+)(api\/)+/i, '/');

  const finalUrl = `${origin}${apiPath}${cleanPath}`;
  return finalUrl;
};